import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import Shield from "../components/Shield";
import uploadLog from "../modules/uploadLog";
import AccordionNode from "./AccordionNode";
import { resolveConfig } from "./faqConfig";
import { getSections, getArticles } from "./zendeskApi";
import { faqClose, setBackHandler } from "./faqBridge";
import { Container, Spinner, ErrorState, FaqHeader, EmailFooter, DebugViewport } from "./faqUi";
import "../css/notice.css";
import "../css/faq.css";

// Fetches a list via `load()` and renders it, with loading / empty / error +
// retry states. `load` should return a Promise of an array.
const ListLoader = ({ load, renderItem, emptyMsg }) => {
  const [state, setState] = useState({ status: "loading", items: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setState({ status: "loading", items: [] });
    load()
      .then((items) => alive && setState({ status: "done", items: items || [] }))
      .catch(() => alive && setState({ status: "error", items: [] }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (state.status === "loading") return <Spinner />;
  if (state.status === "error") {
    return <ErrorState message="불러오기에 실패했습니다." onRetry={() => setAttempt((a) => a + 1)} />;
  }
  if (!state.items.length) return <div className="faq-state">{emptyMsg}</div>;
  return <>{state.items.map(renderItem)}</>;
};

// Articles of a section: each one links to the detail page, carrying the
// current query string (lang/edition/server) so the detail resolves the same
// host/locale.
const ArticlesList = ({ api, sectionId, search }) => (
  <ListLoader
    load={() => getArticles(api, sectionId)}
    emptyMsg="아티클이 없습니다."
    renderItem={(a) => (
      <Link key={a.id} className="faq-article-link" to={"/faq/articles/" + a.id + search}>
        <span className="faq-article-link__title">{a.name}</span>
        <svg className="faq-article-link__arrow" viewBox="0 0 24 24" width="16" height="16">
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 6l6 6-6 6"
          />
        </svg>
      </Link>
    )}
  />
);

const SectionsList = ({ api, categoryId, search }) => (
  <ListLoader
    load={() => getSections(api, categoryId)}
    emptyMsg="섹션이 없습니다."
    renderItem={(s) => (
      <AccordionNode
        key={s.id}
        title={s.name}
        levelClass="level-section"
        renderBody={() => <ArticlesList api={api} sectionId={s.id} search={search} />}
      />
    )}
  />
);

const FaqList = (props) => {
  const config = resolveConfig(props.location.search);
  const isRtl = config.locale && config.locale.split("-")[0] === "ar";

  const errorMessage = useCallback(() => {
    if (config.error === "server") {
      return "지원하지 않는 server 값입니다 (prod 또는 test).";
    }
    if (config.error === "category") {
      return (
        "지원하지 않는 설정 조합입니다 (version=" +
        config.version +
        ", region=" +
        config.region +
        ", platform=" +
        config.platform +
        "). category 파라미터로 직접 지정할 수 있습니다."
      );
    }
    return "";
  }, [config]);

  useEffect(() => {
    if (config.error) {
      uploadLog(window.location.href, "ERROR", errorMessage());
    }
  }, [config.error, errorMessage]);

  // The list is the root view: device back / header back close the webview.
  useEffect(() => {
    setBackHandler(faqClose);
  }, []);

  return (
    <Container>
      <Shield>
        <Helmet>
          <title>KineMaster FAQ</title>
        </Helmet>
        <FaqHeader locale={config.locale} onBack={faqClose} showBack={config.back} variant="list" />

        <div className={"faq-wrap" + (isRtl ? " textR" : "")}>
          {config.error ? (
            <ErrorState message={errorMessage()} />
          ) : (
            <>
              <SectionsList
                api={config.api}
                categoryId={config.categoryId}
                search={props.location.search}
              />
              <EmailFooter locale={config.locale} category="Common" />
            </>
          )}
        </div>
        <DebugViewport />
      </Shield>
    </Container>
  );
};

export default FaqList;
